import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Box,
  Text,
  Flex,
  Grid,
  VStack,
  HStack,
  Tag,
  TagLabel,
  useColorModeValue,
  Badge,
  Divider,
  Icon,
} from '@chakra-ui/react';
import { FiSearch, FiFileText, FiPieChart, FiList, FiCheckCircle, FiInfo, FiEdit3, FiMessageSquare, FiRepeat, FiTrendingUp } from 'react-icons/fi';

// Define the suggestion group interface
interface SuggestionItem {
  text: string;
  icon: React.ComponentType;
}

interface SuggestionGroup {
  name: string;
  icon: React.ComponentType;
  items: SuggestionItem[];
}

interface PromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  suggestionGroups: SuggestionGroup[];
  onSelectPrompt: (prompt: string) => void;
}

const PromptModal: React.FC<PromptModalProps> = ({
  isOpen,
  onClose,
  suggestionGroups,
  onSelectPrompt,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [filteredItems, setFilteredItems] = useState<Array<{ text: string; icon: React.ComponentType; group: string }>>([]);
  const initialFocusRef = useRef<HTMLInputElement>(null);

  // Colors
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const cardHoverBg = useColorModeValue('gray.100', 'gray.700');
  const badgeBg = useColorModeValue('blue.50', 'blue.900');
  const badgeColor = useColorModeValue('blue.600', 'blue.200');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Filter prompts based on search and selected group
  useEffect(() => {
    // Flatten all items from all groups
    const allItems = suggestionGroups.flatMap(group => 
      group.items.map(item => ({ 
        ...item, 
        group: group.name 
      }))
    );
    
    // Apply filters
    const filtered = allItems.filter(item => {
      const matchesSearch = !searchTerm || item.text.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesGroup = !selectedGroup || item.group === selectedGroup;
      return matchesSearch && matchesGroup;
    });
    
    setFilteredItems(filtered);
  }, [searchTerm, selectedGroup, suggestionGroups]);

  // Handle prompt selection
  const handlePromptClick = (prompt: string) => {
    onSelectPrompt(prompt);
    onClose();
  };

  // Get icon for a group
  const getGroupIcon = (groupName: string) => {
    const group = suggestionGroups.find(g => g.name === groupName);
    return group ? group.icon : FiFileText;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" initialFocusRef={initialFocusRef}>
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent maxH="85vh">
        <ModalHeader>Prompt Library</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody overflow="hidden" display="flex" flexDirection="column">
          <Box mb={4}>
            <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
              Search or filter prompts by category
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray.300" />
              </InputLeftElement>
              <Input
                ref={initialFocusRef}
                placeholder="Search prompts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Box>
          
          <HStack mb={4} spacing={2} overflowX="auto" py={1}>
            <Tag
              size="md"
              borderRadius="full"
              variant={selectedGroup === null ? 'solid' : 'outline'}
              colorScheme="blue"
              cursor="pointer"
              onClick={() => setSelectedGroup(null)}
            >
              <TagLabel>All Categories</TagLabel>
            </Tag>
            {suggestionGroups.map((group) => (
              <Tag
                key={group.name}
                size="md"
                borderRadius="full"
                variant={selectedGroup === group.name ? 'solid' : 'outline'}
                colorScheme="blue"
                cursor="pointer"
                onClick={() => setSelectedGroup(group.name)}
              >
                <Box mr={1}>
                  <Icon as={group.icon} />
                </Box>
                <TagLabel>{group.name}</TagLabel>
              </Tag>
            ))}
          </HStack>
          
          <Box flex="1" overflowY="auto" pr={2}>
            {filteredItems.length > 0 ? (
              <Grid 
                templateColumns="repeat(auto-fill, minmax(250px, 1fr))" 
                gap={4} 
                width="100%"
              >
                {filteredItems.map((item, idx) => (
                  <Box
                    key={idx}
                    p={4}
                    bg={cardBg}
                    borderRadius="md"
                    boxShadow="sm"
                    cursor="pointer"
                    transition="all 0.2s"
                    borderWidth="1px"
                    borderColor={borderColor}
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    _hover={{
                      transform: 'translateY(-2px)',
                      boxShadow: 'md',
                      bg: cardHoverBg
                    }}
                    onClick={() => handlePromptClick(item.text)}
                  >
                    <Flex align="center" mb={3}>
                      <Box mr={2} color="blue.500">
                        <Icon as={item.icon} boxSize={5} />
                      </Box>
                      <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                        {item.text}
                      </Text>
                    </Flex>
                    
                    <Tag size="sm" colorScheme="blue" variant="subtle" alignSelf="flex-start" mt="auto">
                      <Box mr={1}>
                        <Icon as={getGroupIcon(item.group)} boxSize={3} />
                      </Box>
                      <TagLabel fontSize="xs">{item.group}</TagLabel>
                    </Tag>
                  </Box>
                ))}
              </Grid>
            ) : (
              <Flex justify="center" align="center" h="200px" direction="column">
                <Icon as={FiSearch} boxSize={8} color="gray.400" mb={4} />
                <Text color="gray.500">No matching prompts found</Text>
              </Flex>
            )}
          </Box>
        </ModalBody>

        <ModalFooter borderTop="1px" borderColor={borderColor}>
          <Text fontSize="xs" color="gray.500" mr="auto">
            {filteredItems.length} prompt{filteredItems.length !== 1 ? 's' : ''} found
          </Text>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PromptModal; 